// cmds/ScoringCmds.js
/**
 * @fileoverview
 *
 * @typedef {import('BMapsModel').Compound} Compound
 * @typedef {import('BMapsModel').CaseData} CaseData
 * @typedef {import('BMapsModel').Residue} Residue
 * @typedef {import('BMapsModel').MapCase} MapCase
 *
 * @typedef ScoringCmds
 * @type {object}
 * @property {scoreCompounds} ScoreCompounds
 */
import { App } from 'BMapsSrc/BMapsApp';
import { UserActions, UserCmd } from 'BMapsCmds/UserCmd';
import { DockingReference } from 'BMapsSrc/data_tools';
import { pointsCentroid, pointDistanceSquared } from 'BMapsUtil/atom_distance_utils';
import { ensureArray } from 'BMapsUtil/js_utils';
import { MoleculeLoadOptions, MolDataSource } from 'BMapsSrc/utils';
import { gatherEnergyErrors } from 'BMapsCmds/cmds_common';
import { fetchLID } from 'BMapsUI/ProLIFApi';
import { AtomGroup, BindingSite, Compound } from 'BMapsModel';
import { formResidueKey, PdbData } from 'BMapsSrc/util/pdb';
import { EventBroker } from 'BMapsSrc/eventbroker';
import { withPausedRedisplay } from 'BMapsSrc/util/display_utils';
import { getPreference } from 'BMapsRedux/prefs/access';

/** @type {ScoringCmds} */
export const ScoringCmds = {
    ScoreCompounds: new UserCmd('ScoreCompounds', scoreCompounds),
    FetchProLifData: new UserCmd('FetchProLifData', fetchProLifData),
    GetProteinPdb: new UserCmd('GetProteinPdb', getProteinPdb),
};

/* IMPLEMENTATION */

/**
 *
 * @param {{ compounds: Compound[], proteins: MapCase[], scoringParams: object }}
 * @return {Promise<{
 * results: Map<MapCase, { topPose: Compound, topScore: number, errors: string[] }
 * errors: {name: string, message: string}[]
 * }>>}
 */
async function scoreCompounds({ compounds, proteins, scoringParams={} }) {
    /** @type {Map<MapCase, {topPose: Compound, topScore: number}} */
    const results = new Map();
    const errors = [];

    // take a snap shot of what compounds are currently loaded before scoring.
    const originalCmpds = [...App.Workspace.getLoadedCompounds()];

    // Ensure all compounds exist on all proteins
    await loopOnProteins(proteins, async (mapCase) => {
        const caseData = App.Workspace.lookupCaseData(mapCase);
        for (const compound of compounds) {
            await ensureCompoundOnProtein(compound, caseData);
        }
    });

    // Score the compounds on the proteins
    await loopOnProteins(proteins, async (mapCase) => {
        for (const compound of compounds) {
            const score = await scoreCompoundForOne({ compound, mapCase, scoringParams });
            // If an error causes a compound to not get scored add it to the errors array.
            if (!score.topPose || !score.topScore) {
                errors.push(
                    `Unable to score ${compound.displayName} on ${mapCase.getShortName()}`,
                    ...score.errors
                );
                continue;
            }
            // If the compound still scores even though
            // it had an error just add the error to results.
            let perCompoundMap = results.get(compound);
            if (!perCompoundMap) {
                perCompoundMap = new Map();
                results.set(compound, perCompoundMap);
            }

            perCompoundMap.set(mapCase, score);
        }
    });

    // Cleanup
    const toRemove = [];
    const toGetEnergies = [];
    for (const perCompoundMap of results.values()) {
        for (const score of perCompoundMap.values()) {
            for (const pose of score.scoringPoses) {
                if (originalCmpds.includes(pose)) continue; // don't clean up preexisting cmpds
                const keepDockingSites = false; // Can enable for debugging, to see docking refs
                const isDockingSite = score.dockingSites.some((site) => site.pose === pose);
                const keepDockingSite = keepDockingSites && isDockingSite;
                if (pose !== score.topPose && !keepDockingSite) {
                    console.log(`Removing pose ${pose.resSpec} with score ${scorePose(pose).toFixed(2)}, in favor of ${score.topPose.resSpec} with score ${score.topScore.toFixed(2)}`);
                    toRemove.push(pose);
                } else {
                    toGetEnergies.push(pose);
                }
            }
        }
    }

    if (scoringParams.cleanup) {
        for (const pose of toRemove) {
            await UserActions.RemoveCompound(pose);
        }
    }
    // Solvation is not initially calculated, so fetch it for top poses.
    for (const pose of toGetEnergies) await UserActions.GetEnergies(pose);

    return { results, errors };
}

/**
 * This function is to unify calls for both old (>1 connection) and new (1 connection) selectivity.
 * If we have multiple connections, we can take advantage of Promise.all.
 * For a single connection, the server will eventually crash if we try to do too many things in a
 * Promise.all.
 *
 * This function gathers proteins into groups by connection.
 * It does Promise.all across each conn. group, but awaits in a loop for proteins within the group.
 * @param {MapCase[]} proteins
 * @param {function} doFn
 */
async function loopOnProteins(proteins, doFn) {
    const connectionProteinsMap = new Map();
    for (const mapCase of proteins) {
        const { connector } = App.getDataParents(mapCase);
        let connProteinList = connectionProteinsMap.get(connector);
        if (!connProteinList) {
            connProteinList = [];
            connectionProteinsMap.set(connector, connProteinList);
        }
        connProteinList.push(mapCase);
    }

    await Promise.all(
        [...connectionProteinsMap.values()].map(async (proteinList) => {
            for (const protein of proteinList) {
                await doFn(protein);
            }
        })
    );
}

/**
 * @param {{
 * compound: Compound,
 * mapCase: MapCase,
 * scoringParams:{}
 * }} param0
 * @returns
 */
async function scoreCompoundForOne({ compound, mapCase, scoringParams }) {
    const scoringResults = await getScoringCandidates(compound, mapCase, scoringParams);

    const { compounds: scoringPoses, dockingSites=[], errors } = scoringResults;

    scoringPoses.sort(scoreSortFn);

    const topPose = scoringPoses[0];

    const topScore = scorePose(topPose);

    console.log(`Top pose for ${compound.resSpec} against ${mapCase.getNodeName()}: ${topPose?.resSpec}, score ${topScore.toFixed(2)}`);

    return {
        topPose,
        topScore,
        errors,
        scoringPoses,
        dockingSites,
    };
}

/**
 * For a compound and a mapCase, return poses that will be scored, to identify the top pose.
 */
async function getScoringCandidates(compound, mapCase, scoringParams) {
    const caseDataP = App.Workspace.lookupCaseData(mapCase);

    /** @type {Compound[]} */
    const compounds = [];
    const errors = [];

    const { diffdockPoseCount, autodockPoseCount, autodockSpeed } = getScoringParams(scoringParams);

    // If scoring for a different protein, need to copy the compound to the new protein.
    const ensureResults = await ensureCompoundOnProtein(compound, caseDataP);
    const { compound: dockingCompound, errors: ensureErrs } = ensureResults;
    if (ensureErrs) errors.push(...ensureErrs);

    if (dockingCompound) {
        // Ok to consider the docking compound as a scoring candidate?
        compounds.push(dockingCompound);
    } else {
        // Nothing we can do if we can't get the compound onto the target protein
        return { compounds, errors };
    }

    // Start with DiffDock to get pose candidates and docking references for autodock
    const diffdockParams = { dockingProgram: 'diffdock', poseCount: diffdockPoseCount };
    const diffdockResults = await dockAndMinimizeForScoring(dockingCompound, diffdockParams);
    const { compounds: diffdockPoses, errors: diffdockErrors } = diffdockResults;

    compounds.push(...diffdockPoses);
    errors.push(...diffdockErrors);

    // Autodock
    const dockingSites = getDockingSites(caseDataP, diffdockPoses);

    await Promise.all(dockingSites.map(async (dockingSite) => {
        const { dockingReference } = dockingSite;
        const autodockParams = {
            dockingProgram: 'autodock',
            speedArg: autodockSpeed,
            poseCount: autodockPoseCount,
            boxParams: { refObj: dockingReference },
        };
        const autodockResults = await dockAndMinimizeForScoring(dockingCompound, autodockParams);
        const { compounds: autodockPoses, errors: autodockErrors } = autodockResults;
        compounds.push(...autodockPoses);
        errors.push(...autodockErrors);
    }));

    return { compounds, dockingSites, errors };
}

function getScoringParams(scoringParams={}) {
    const ret = {
        diffdockPoseCount: 10,
        autodockPoseCount: 10,
        autodockSpeed: 'accurate',
    };

    if (scoringParams.quick) {
        ret.diffdockPoseCount = 2;
        ret.autodockPoseCount = 2;
        ret.autodockSpeed = 'faster';
    }
    if (scoringParams.diffdockPoseCount) ret.diffdockPoseCount = scoringParams.diffdockPoseCount;
    if (scoringParams.autodockPoseCount) ret.autodockPoseCount = scoringParams.autodockPoseCount;
    if (scoringParams.autodockSpeed) ret.autodockSpeed = scoringParams.autodockSpeed;
    return ret;
}

/**
 * @param {Compound} compound
 * @param {CaseData} caseDataP
 * @returns {Promise<{compound: Compound} | {errors: string[]}>}
 */
async function ensureCompoundOnProtein(compound, caseDataP) {
    const compoundKey = compound.getUniqueStructId();

    const lookupCompounds = App.Workspace.getLoadedCompounds().filter((pose) => {
        const poseKey = pose.getUniqueStructId();
        return (poseKey === compoundKey && App.getDataParents(pose).mapCase === caseDataP.mapCase);
    });

    if (lookupCompounds.length > 0) {
        return { compound: lookupCompounds[0] };
    }

    const molSource = new MolDataSource({
        sourceType: MolDataSource.Types.ScoreCompound,
        molFormat: 'mol',
        molData: compound.getMol2000(),
    });
    // Note: this MolDataSource usage will not copy the properties or heritage from compound.
    // Ideally, we could assign sourceCmpds, which would cause it to get copied over after loading.
    // However, the mechanism in ConnectedDataCmds currently assumes coordinates were preserved,
    // and for scoring we need to put the new compound "outside the protein" to avoid clashing.
    const { compounds, errors } = await UserActions.LoadMolData(
        molSource,
        new MoleculeLoadOptions(),
        caseDataP,
    );
    return (errors?.length > 0) ? { errors } : { compound: compounds[0] };
}

function getDockingSites(caseDataProtein, diffdockPoses) {
    const sites = [];
    const siteSeparationDistanceSq = 8**2;
    for (const pose of diffdockPoses) {
        const centroid = pointsCentroid(pose.getAtoms());
        if (sites.every(
            ({ centroid: site }) => pointDistanceSquared(centroid, site) > siteSeparationDistanceSq
        )) {
            sites.push({
                pose,
                centroid,
                dockingReference: DockingReference.ForCompound(pose),
            });
        }
    }

    console.log(`ScoreCompound: docking sites: ${sites.map((s) => `${s.pose.resSpec}:${s.centroid.map((x) => x.toFixed(1))}`).join('; ')}`);
    return sites;
}

function scorePose(pose) {
    return pose?.energyInfo.getEnergyScore() || 100;
}

function scoreSortFn(a, b) {
    return scorePose(a) - scorePose(b);
}

async function dockAndMinimize(cmpds, dockingParams, { otherDockOptions, minimizationOptions }={}) {
    const compounds = ensureArray(cmpds);
    const {
        compounds: dockPoses, errors: dockErrs, groups,
    } = await UserActions.SubmitDocking(compounds, dockingParams, otherDockOptions);
    const minResults = await UserActions.EnergyMinimize(dockPoses, minimizationOptions);
    const { compounds: minPoses, errors: rawMinErrs, energies } = minResults;
    minPoses.sort(scoreSortFn);
    const cmpdErrMap = gatherEnergyErrors(
        dockPoses, minPoses, energies,
        rawMinErrs, minimizationOptions
    );
    const minErrs = [];
    for (const [cmpd, cmpdErrs] of cmpdErrMap.entries()) {
        if (cmpdErrs.length === 0) continue;
        for (const cmpdErr of cmpdErrs) {
            minErrs.push(`${cmpd.displayName} energy error: ${cmpdErr}`);
        }
    }
    console.log(`ScoreCompound: docked and minimized: ${minPoses.map((p) => `${p.resSpec}:${scorePose(p)}`).join('; ')}`);
    return { compounds: minPoses, groups, errors: [...dockErrs, ...minErrs] };
}

async function dockAndMinimizeForScoring(cmpds, dockingParams,
    { otherDockOptions: otherDockOptionsIn, minimizationOptions: minimizationOptionsIn }={}) {
    const otherDockOptions = { skipGrouping: true, ...otherDockOptionsIn };
    const minimizationOptions = {
        skipSolvation: true,
        ...minimizationOptionsIn,
    };
    return dockAndMinimize(cmpds, dockingParams, { otherDockOptions, minimizationOptions });
}

/**
 * Fetch ProLife data for the given compound among binding site residues.
 * First determine binding site, and convert to PDB text for the residues.
 * @param {{ compound: Compound, parameters: { width?, height? }}} param0
 * @returns {{ html: string, error: string }}
 */
async function fetchProLifData({ compound, parameters }={}) {
    const pdbOptions = {
        bindingSiteDistance: 15,
        noPauseRedisplay: getPreference('PiPiAlgorithm') === 'prolif',
    };
    const { pdb, error, pdbObj } = await getProteinPdb(compound, pdbOptions);

    const noPdbMsg = 'Load a protein to see a ligand interaction diagram.'; // matches ExportProLIF

    const sdf = compound.getSDF({ propInclusion: 'none' });
    const noPdb = (!error && !pdb) || pdb?.indexOf('The export-selection command found nothing selected.') > 0;
    const haveError = error || pdb?.startsWith('Caught error processing text command request export-selection');

    const params = {
        width: parameters?.width ?? '100%',
        height: parameters?.height ?? '300px',
    };

    if (noPdb) return { error: noPdbMsg };
    if (haveError) {
        console.warn(`ProLIF LID prep error: ${error || pdb }`);
        return { error: 'This system failed to be prepared for a ligand interaction diagram.' };
    }

    // Call API and set LID html
    try {
        const result = await fetchLID(pdb, sdf, params);
        if (result.html) {
            return { html: result.html, pdbObj };
        } else {
            console.warn(`Error from LID service: ${result.error}`);
            return { error: 'The Ligand Interaction Diagram service failed to prepare the diagram for this structure.' };
        }
    } catch (errorObj) {
        console.warn(`Error communicating with LID service: ${errorObj.message}`);
        return { error: 'There was a problem communicating with the Ligand Interaction Diagram service.' };
    }
}

/**
 * Get a PDB string for a compounds binding site residues.
 * @param {Compound|CaseData} input
 * @param {{ method: string, bindingSiteDistance: number, noPauseRedisplay: boolean }} options
 * @returns {{ pdb: string, error: string, pdbObj: PdbData } |
 *     Promise<{ pdb: string, error: string, pdbObj: PdbData }>
 * }
 */
async function getProteinPdb(input, options={}) {
    const { method: methodIn, bindingSiteDistance, noPauseRedisplay } = options;
    const method = methodIn || getPreference('ProteinPdbMethod');

    if (method === 'local') {
        return localProteinPdb(input, { bindingSiteDistance });
    } else if (method === 'server') {
        return serverProteinPdb(input, { bindingSiteDistance, noPauseRedisplay });
    }
    return { pdb: '', error: `Invalid PDB method: ${method}` };
}

/**
 * Get PDB string for the compound's binding site residues, using BMaps internal logic.
 * @param {Compound|CaseData} input
 * @param {{ bindingSiteDistance: number }} options
 * @returns {{ pdb: string, error: , pdbObj: PdbData }}
 */
function localProteinPdb(input, { bindingSiteDistance }={}) {
    const { caseData } = App.getDataParents(input);
    let residues = caseData.getResidues();
    if (bindingSiteDistance != null && input instanceof Compound) {
        const compound = input;
        const bSite = BindingSite.calculate(App.Workspace, { compound }, { bindingSiteDistance });
        residues = bSite.getComponentsInRange(residues);
    }
    const pdbData = getPdbDataForResidues(residues);
    return { pdb: pdbData.format(), error: '', pdbObj: pdbData };
}

/**
 * Get PDB string for the compound's binding site residues, using bfd-server export-selection.
 * @param {Compound|CaseData} input
 * @param {{ bindingSiteDistance: number, noPauseRedisplay: boolean }} options
 * @returns {Promise<{ pdb: string, error: string, pdbObj: PdbData }>}
 */
async function serverProteinPdb(input, { bindingSiteDistance, noPauseRedisplay }={}) {
    let pdb;
    let error;
    const { caseData } = App.getDataParents(input);
    let query = '(protein or rna or dna)';
    if (bindingSiteDistance != null && input instanceof Compound) {
        const compound = input;
        query += ` and within (${bindingSiteDistance} ${compound.resSpec}); roundup`;
    }
    const exportPromise = UserActions.ExportQueryToFormat(query, 'pdb', caseData);
    if (noPauseRedisplay) {
        // The redraw request below redraws the entire scene, including pi-pi stacks.
        // If ProLIF is used for detecting pi-pi interactions, redrawing the pi-pi stacks will
        // fetch ProLIF again, calling this function again for PDB data, redrawing the scene...
        // To avoid an infinite loop, don't pause & redraw when using ProLIF for pi-pi detection.
        // The atoms will briefly flash when they are selected, but better than infinite loop.
        console.warn('When using ProLIF pi-pi detection and bfd-server pdb generation, atoms flash during the LID request preparation.');
        ({ data: pdb, error } = await exportPromise);
        return { pdb, error, pdbObj: new PdbData(pdb) };
    } else {
        ({ data: pdb, error } = await withPausedRedisplay(exportPromise));
        // Pause redisplay to avoid flashing selected atoms.
        // Make sure the scene is redrawn afterwards, necessary because if the user
        // switches compounds with just the right timing, we could end up with a blank screen.
        // May occasionally cause slight delay, but since fetch is debounced, should be fine.
        EventBroker.publish('redrawSceneRequest');
        return { pdb, error, pdbObj: new PdbData(pdb) };
    }
}

/**
 * Get PDB text data for a set of residues, using PdbData class.
 * @param {Residue} residuesIn
 * @returns {string}
 */
function getPdbDataForResidues(residuesIn) {
    const residues = sortResidues(residuesIn);
    const atoms = AtomGroup.atomsInAtomGroups(residues);
    const pdbRes = residues.map((residue) => ({
        hetflag: residue.residue.hetflag,
        resName: residue.resn,
        chainId: residue.chain,
        seqNum: residue.resi,
        iCode: residue.residue.parentInsertionCode,
        atoms: residue.getAtoms().filter((a) => atoms.includes(a)).map((atom) => ({
            atomName: atom.atom,
            altLoc: atom.altLoc,
            x: atom.origX,
            y: atom.origY,
            z: atom.origZ,
            element: atom.elem,
            connections: getAtomConnectionsForPdb(atom, atoms),
        })),
    })).filter(Boolean);
    return new PdbData({ residues: pdbRes });
}

function getAtomConnectionsForPdb(atom, allowedAtoms) {
    const relevantAtom = (a) => a.hetflag || ['C', 'N', 'P', 'O3*'].includes(a.atom) || a.elem === 'S';
    if (!relevantAtom(atom)) return [];
    const connections = atom.bondedAtoms
        .filter((a) => !allowedAtoms || allowedAtoms.includes(a))
        .filter(relevantAtom).map((oat) => ({
            resKey: formResidueKey(oat.resn, oat.resi, oat.chain, { iCode: oat.iCode }),
            atomName: oat.atom,
            altLoc: oat.altLoc,
        }));
    return connections;
}

/**
 * Sort residues by chain, sequence number, and insertion code.
 * @param {Residue[]} residuesIn
 * @returns {Residue[]}
 */
function sortResidues(residuesIn) {
    const residues = [...residuesIn];
    residues.sort((a, b) => {
        if (a.chain !== b.chain) return a.chain.localeCompare(b.chain);
        if (a.resi !== b.resi) return a.resi - b.resi;
        if (a.residue.parentInsertionCode !== b.residue.parentInsertionCode) {
            if (a.residue.parentInsertionCode == null) return -1;
            return a.residue.parentInsertionCode.localeCompare(b.residue.parentInsertionCode);
        }
        return 0;
    });
    return residues;
}
